import React, { useEffect, useRef, useState } from "react";

/**
 * Generic enter/exit animation for a vertical stack of banners.
 *
 * Why this exists: banners on the site appear/disappear for many reasons —
 * a channel hits its limit, a channel is disconnected, the user dismisses a
 * warning, a freshness count drops to zero. Previously each banner hand-rolled
 * its own dismiss animation, so it only animated on the ONE path it knew about
 * and snapped in/out on every other. This wrapper makes the animation a
 * property of *presence*: whenever a banner's key enters or leaves the list —
 * for ANY reason — it fades + expands in, or fades + collapses out.
 *
 * Usage: build `items` as `{ key, node }[]` from your live state and render
 * `<AnimatedBannerList items={items} />`. Keep the banner components "dumb"
 * (just markup); don't animate inside them — presence here owns it.
 *
 * Notes:
 *  - `items` may be rebuilt every render (new array / new elements); the
 *    presence diff keys off the set+order of `key`s, so changing only a
 *    banner's content (e.g. a usage %) re-renders it without re-animating.
 *  - Each banner keeps whatever bottom margin it already has for spacing; the
 *    slot wrapper uses overflow:hidden so that margin collapses with the
 *    height on exit.
 */

export type BannerPresenceItem = { key: string; node: React.ReactNode };

const DURATION_MS = 320;

const BannerSlot: React.FC<{
  present: boolean;
  onExited: () => void;
  children: React.ReactNode;
}> = ({ present, onExited, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const didEnter = useRef(false);

  // Enter on first mount: 0 → natural height, fade + slight slide down.
  useEffect(() => {
    const el = ref.current;
    if (!el || didEnter.current) return;
    didEnter.current = true;
    el.style.height = "auto";
    const target = el.getBoundingClientRect().height;
    el.style.height = "0px";
    el.style.opacity = "0";
    el.style.transform = "translateY(-6px)";
    void el.offsetHeight; // force reflow so the transition runs from 0
    el.style.transition = `height ${DURATION_MS}ms ease, opacity ${DURATION_MS}ms ease, transform ${DURATION_MS}ms ease`;
    el.style.height = `${target}px`;
    el.style.opacity = "1";
    el.style.transform = "translateY(0)";
    const t = window.setTimeout(() => {
      const e2 = ref.current;
      if (e2) { e2.style.height = "auto"; e2.style.transition = ""; e2.style.transform = ""; }
    }, DURATION_MS);
    return () => window.clearTimeout(t);
  }, []);

  // Exit when presence flips false: current height → 0, fade + slide up.
  useEffect(() => {
    if (present) return;
    const el = ref.current;
    if (!el) { onExited(); return; }
    const current = el.getBoundingClientRect().height;
    el.style.height = `${current}px`;
    el.style.overflow = "hidden";
    void el.offsetHeight;
    el.style.transition = `height ${DURATION_MS}ms ease, opacity ${DURATION_MS}ms ease, transform ${DURATION_MS}ms ease`;
    el.style.height = "0px";
    el.style.opacity = "0";
    el.style.transform = "translateY(-6px)";
    const t = window.setTimeout(onExited, DURATION_MS);
    return () => window.clearTimeout(t);
  }, [present, onExited]);

  return (
    <div ref={ref} style={{ overflow: "hidden" }}>
      {children}
    </div>
  );
};

export const AnimatedBannerList: React.FC<{ items: BannerPresenceItem[] }> = ({ items }) => {
  // Latest node per key, refreshed every render so live banners reflect new
  // content. Exiting keys keep their last node until their exit completes.
  const nodeMap = useRef<Map<string, React.ReactNode>>(new Map());
  const liveKeys = items.map((i) => i.key);
  items.forEach((i) => nodeMap.current.set(i.key, i.node));

  // Render order with presence flags. Exiting items stay in place (present=false)
  // until their collapse finishes, then drop out.
  const [order, setOrder] = useState<Array<{ key: string; present: boolean }>>(
    () => items.map((i) => ({ key: i.key, present: true })),
  );

  // Re-diff only when the SET/ORDER of keys changes, not on content updates —
  // otherwise the new-array-every-render parent would loop us.
  const signature = liveKeys.join("|");
  useEffect(() => {
    const live = new Set(liveKeys);
    setOrder((prev) => {
      const prevKeys = new Set(prev.map((p) => p.key));
      const next = prev.map((p) => ({ key: p.key, present: live.has(p.key) }));
      for (const k of liveKeys) {
        if (!prevKeys.has(k)) next.push({ key: k, present: true });
      }
      return next;
    });
  }, [signature]);

  const handleExited = (key: string) => {
    nodeMap.current.delete(key);
    setOrder((prev) => prev.filter((p) => p.key !== key));
  };

  return (
    <>
      {order.map((o) => (
        <BannerSlot key={o.key} present={o.present} onExited={() => handleExited(o.key)}>
          {nodeMap.current.get(o.key)}
        </BannerSlot>
      ))}
    </>
  );
};

export default AnimatedBannerList;
